// Builds the GA4 `purchase` event for a paid receipt: total team-store sale (what the fan paid),
// with item_category ticket / product / form and order-level team tags (same camelCase parameter
// names as the other sports_client_website events, so existing GA custom dimensions apply).

export type PurchaseTeamInfo = {
    teamId: string;
    teamName: string;
    organizationId: string;
    organizationName: string;
    sportsTypeName: string;
};

// GA4 ecommerce field names are snake_case by spec
/* eslint-disable camelcase */
type PurchaseItem = {
    item_name: string;
    item_category: 'ticket' | 'product' | 'form';
    item_variant?: string;
    price: number;
    quantity: number;
};
/* eslint-enable camelcase */

const SALE_TYPE_ORDER: PurchaseItem['item_category'][] = ['ticket', 'product', 'form'];

const toUnits = (cents: number): number => {
    return Math.round(cents) / 100;
};

const joinParts = (parts: any[]): string => {
    return parts.filter((part) => { return typeof part === 'string' && part.length > 0; }).join(' - ');
};

const withVariant = (item: PurchaseItem, variant: string): PurchaseItem => {
    return variant.length > 0 ? { ...item, item_variant: variant } : item;
};

export const getPurchaseItems = (receipt: any): PurchaseItem[] => {
    const items: PurchaseItem[] = [];
    (receipt.tickets || []).forEach((ticket: any) => {
        (ticket.lines || []).forEach((line: any) => {
            const quantity = Number(line.quantity) || 0;
            if (quantity <= 0) { return; }
            items.push(withVariant({
                item_name: ticket.eventTitle || 'Ticket',
                item_category: 'ticket',
                price: typeof line.unitAmount === 'number' ? toUnits(line.unitAmount) : 0,
                quantity,
            }, joinParts([line.ticketTitle, line.ticketPriceTitle])));
        });
    });
    (receipt.products || []).forEach((product: any) => {
        const quantity = Number(product.quantity) || 0;
        if (quantity <= 0) { return; }
        const options = (product.options || []).map((option: any) => { return `${option.title}: ${option.value}`; }).join(', ');
        items.push(withVariant({
            item_name: product.title || 'Product',
            item_category: 'product',
            price: typeof product.amount === 'number' ? toUnits(product.amount / quantity) : 0,
            quantity,
        }, options));
    });
    if (receipt.form && receipt.paid) {
        items.push(withVariant({
            item_name: receipt.form.title || 'Form',
            item_category: 'form',
            price: typeof receipt.total === 'number' ? toUnits(receipt.total) : 0,
            quantity: 1,
        }, receipt.form.paymentOption || ''));
    }
    return items;
};

export const getSaleType = (items: PurchaseItem[]): string => {
    return SALE_TYPE_ORDER.filter((category) => { return items.some((item) => { return item.item_category === category; }); }).join('+');
};

// Only report receipts opened soon after the order (the buyer's post-checkout visit), so later opens of the
// emailed link on another device or by a team admin from the portal don't count the sale again.
export const PURCHASE_REPORT_WINDOW_MS = 24 * 60 * 60 * 1000;

// Returns null when the receipt should not be reported (unpaid, free, stale, or missing order data).
export const buildPurchaseEventParams = (receipt: any, team: PurchaseTeamInfo, now: number = Date.now()): Record<string, any> | null => {
    if (!receipt || typeof receipt.createdAt !== 'number' || now - receipt.createdAt > PURCHASE_REPORT_WINDOW_MS) { return null; }
    if (receipt.paid !== true || typeof receipt.total !== 'number' || receipt.total <= 0) { return null; }
    if (typeof receipt.orderNumber !== 'string' || receipt.orderNumber.length === 0) { return null; }
    if (typeof receipt.currency !== 'string' || receipt.currency.length === 0) { return null; }
    const items = getPurchaseItems(receipt);
    return {
        transaction_id: receipt.orderNumber,
        value: toUnits(receipt.total),
        currency: receipt.currency.toUpperCase(),
        items,
        saleType: getSaleType(items),
        teamId: team.teamId,
        teamName: team.teamName,
        organizationId: team.organizationId,
        organizationName: team.organizationName,
        sportsTypeName: team.sportsTypeName,
    };
};

export const getPurchaseSentStorageKey = (token: string): string => {
    return `optimx_purchase_sent_${token}`;
};
